const KBCG_ROOT_SELECTOR = 'kaching-cart[kaching-cart-props]';
const KBCG_ITEM_SELECTOR = '.kaching-cart-item';
const KBCG_SUBSCRIBE_SELECTOR = '.kaching-cart-item__selling-plan-upgrade';
const KBCG_REMOVE_SELECTOR = '.kaching-cart-item__remove-btn';
const KBCG_BUSY_ATTRIBUTE = 'data-kaching-bundle-cart-guard-busy';

let kbcgBusy = false;

function kbcgRootPath() {
  return window.Shopify?.routes?.root || '/';
}

function kbcgUrl(path) {
  return new URL(`${kbcgRootPath()}${path}`, window.location.origin).href;
}

function kbcgCleanFetch(path, options = {}) {
  const frameWindow = document.getElementById('kaching-cart-iframe-for-clean-apis')?.contentWindow;
  const fetcher = frameWindow?.fetch;
  const url = kbcgUrl(path);

  if (typeof fetcher === 'function') {
    return fetcher.call(frameWindow, url, options);
  }

  return window.fetch(url, options);
}

async function kbcgJson(path, options = {}, clean = true) {
  const response = clean
    ? await kbcgCleanFetch(path, options)
    : await window.fetch(kbcgUrl(path), options);

  if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
  return response.json();
}

function kbcgBundle(line) {
  const raw = line.properties?.__kaching_bundles;
  if (!raw) return null;
  if (typeof raw === 'object') return raw;

  try {
    return JSON.parse(raw);
  } catch (error) {
    return null;
  }
}

function kbcgPlanId(line) {
  return (
    line.selling_plan_allocation?.selling_plan?.id ||
    line.selling_plan_allocation?.selling_plan_id ||
    null
  );
}

function kbcgVariantId(element) {
  const link = element.querySelector('a[href*="variant="]');
  if (!link) return null;

  try {
    return Number(new URL(link.href, window.location.origin).searchParams.get('variant'));
  } catch (error) {
    return null;
  }
}

function kbcgElementHasPlan(element) {
  const value = element.getAttribute('data-has-selling-plan');
  if (value === 'true') return true;
  if (value === 'false') return false;
  return /delivered\s+every/i.test(element.textContent || '');
}

function kbcgReadEntries(sourceRoot = null) {
  const root = sourceRoot || document.querySelector(KBCG_ROOT_SELECTOR);
  if (!root) return [];

  let state;

  try {
    state = JSON.parse(root.getAttribute('kaching-cart-props') || '{}');
  } catch (error) {
    return [];
  }

  const lines = state?.cart?.items;
  if (!Array.isArray(lines)) return [];

  const elements = Array.from(root.querySelectorAll(KBCG_ITEM_SELECTOR));
  const unused = new Set(elements);

  return lines.map((line) => {
    const bundle = kbcgBundle(line);
    const isFree = Boolean(bundle?.bxgy);
    const hasPlan = Boolean(kbcgPlanId(line));
    const matches = (candidate) => {
      if (!unused.has(candidate)) return false;
      if (Number(candidate.getAttribute('data-product-id')) !== Number(line.product_id)) return false;

      const variantId = kbcgVariantId(candidate);
      if (variantId && variantId !== Number(line.variant_id)) return false;
      return kbcgElementHasPlan(candidate) === hasPlan;
    };

    let element = elements.find((candidate) => {
      if (!matches(candidate)) return false;
      const showsFree = /(^|\s)FREE($|\s)/i.test(candidate.textContent || '');
      return showsFree === isFree;
    });

    if (!element) element = elements.find(matches);
    if (element) unused.delete(element);

    return { bundle, element, isFree, line };
  });
}

function kbcgSameGroup(entry, selected) {
  return (
    entry.bundle?.id &&
    entry.bundle.id === selected.bundle?.id &&
    Number(entry.line.product_id) === Number(selected.line.product_id) &&
    Number(entry.line.variant_id) === Number(selected.line.variant_id) &&
    kbcgPlanId(entry.line) === kbcgPlanId(selected.line)
  );
}

function kbcgSelection(target) {
  const item = target.closest?.(KBCG_ITEM_SELECTOR);
  if (!item) return null;

  const root = item.closest(KBCG_ROOT_SELECTOR);
  if (!root) return null;

  const entries = kbcgReadEntries(root);
  const selected = entries.find((entry) => entry.element === item) || null;
  if (!selected) return null;

  return { entries, selected };
}

function kbcgFreshGroup(cart, selected) {
  return (cart.items || []).filter((line) => {
    const bundle = kbcgBundle(line);

    return (
      bundle?.id === selected.bundle.id &&
      Number(line.product_id) === Number(selected.line.product_id) &&
      Number(line.variant_id) === Number(selected.line.variant_id) &&
      kbcgPlanId(line) === kbcgPlanId(selected.line)
    );
  });
}

function kbcgRemovalUpdates(lines) {
  return lines.reduce((updates, line) => {
    updates[line.key] = 0;
    return updates;
  }, {});
}

function kbcgItem(line, sellingPlanId = null) {
  const item = {
    id: line.variant_id,
    quantity: line.quantity,
  };

  if (line.properties && Object.keys(line.properties).length > 0) {
    item.properties = { ...line.properties };
  }

  if (sellingPlanId) item.selling_plan = sellingPlanId;
  return item;
}

function kbcgSetBusy(entries, busy) {
  entries.forEach((entry) => {
    if (!entry.element) return;

    if (busy) {
      entry.element.setAttribute(KBCG_BUSY_ATTRIBUTE, 'true');
      entry.element.setAttribute('aria-busy', 'true');
    } else {
      entry.element.removeAttribute(KBCG_BUSY_ATTRIBUTE);
      entry.element.removeAttribute('aria-busy');
    }
  });
}

function kbcgAnnounce(cart, source) {
  const detail = { cart, source };
  document.dispatchEvent(new CustomEvent('cart:updated', { detail }));
  document.dispatchEvent(new CustomEvent('cart:refresh', { detail }));
  window.dispatchEvent(new CustomEvent('cart:updated', { detail }));
}

async function kbcgRefresh(source) {
  let cart = null;

  if (typeof window.kachingCartApi?.refreshCart === 'function') {
    await window.kachingCartApi.refreshCart();
    cart = window.kachingCartApi.getCartData?.() || null;
  }

  if (!cart) {
    cart = await kbcgJson('cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
  }

  kbcgAnnounce(cart, source);
  return cart;
}

async function kbcgRemoveLines(lines) {
  return kbcgJson('cart/update.js', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ updates: kbcgRemovalUpdates(lines) }),
  });
}

async function kbcgAddLines(items) {
  return kbcgJson('cart/add.js', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ items }),
  });
}

function kbcgSellingPlanId(productData, variantId) {
  const variant = productData.variants?.find(
    (candidate) => Number(candidate.id) === Number(variantId)
  );
  const allocation = variant?.selling_plan_allocations?.[0];
  return allocation?.selling_plan_id || allocation?.selling_plan?.id || null;
}

async function kbcgSubscribe(selected, entries) {
  if (kbcgBusy) return;
  kbcgBusy = true;
  kbcgSetBusy(entries, true);

  try {
    const cart = await kbcgJson('cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    const group = kbcgFreshGroup(cart, selected);

    if (!group.some((line) => kbcgBundle(line)?.bxgy) || !group.some((line) => !kbcgBundle(line)?.bxgy)) {
      throw new Error('Paid and free bundle lines were not both found');
    }

    const productData = await kbcgJson(
      `products/${encodeURIComponent(selected.element.getAttribute('data-product-handle'))}.js`,
      { credentials: 'same-origin', headers: { Accept: 'application/json' } }
    );
    const sellingPlanId = kbcgSellingPlanId(productData, selected.line.variant_id);

    if (!sellingPlanId) throw new Error('No selling plan is available for this variant');

    const subscribedItems = group.map((line) => kbcgItem(line, sellingPlanId));
    const rollbackItems = group.map((line) => kbcgItem(line));

    await kbcgRemoveLines(group);

    try {
      await kbcgAddLines(subscribedItems);
    } catch (error) {
      await kbcgAddLines(rollbackItems);
      throw error;
    }

    await kbcgRefresh('kaching-bundle-subscribe');
  } catch (error) {
    console.error('[Kaching bundle subscribe]', error);
  } finally {
    kbcgBusy = false;
    kbcgSetBusy(entries, false);
  }
}

async function kbcgRemove(selected, entries) {
  if (kbcgBusy) return;
  kbcgBusy = true;
  kbcgSetBusy(entries, true);

  try {
    const group = entries.map((entry) => entry.line);

    if (group.length === 0) return;

    await kbcgRemoveLines(group);
    await kbcgRefresh('kaching-bundle-remove');
  } catch (error) {
    console.error('[Kaching bundle remove]', error);
  } finally {
    kbcgBusy = false;
    kbcgSetBusy(entries, false);
  }
}

function kbcgStop(event) {
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();
}

function kbcgHandle(event) {
  const subscribeControl = event.target.closest?.(KBCG_SUBSCRIBE_SELECTOR);
  const removeControl = event.target.closest?.(KBCG_REMOVE_SELECTOR);
  if (!subscribeControl && !removeControl) return;

  if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;

  const selection = kbcgSelection(event.target);
  const selected = selection?.selected;
  if (!selected?.bundle?.id || !selected.bundle.bar) return;

  const entries = selection.entries.filter((entry) => kbcgSameGroup(entry, selected));
  const hasPaid = entries.some((entry) => !entry.isFree);
  const hasFree = entries.some((entry) => entry.isFree);

  if (!hasPaid || !hasFree) return;

  if (removeControl) {
    if (selected.isFree) return;

    kbcgStop(event);
    void kbcgRemove(selected, entries);
    return;
  }

  if (!kbcgPlanId(selected.line)) {
    kbcgStop(event);
    void kbcgSubscribe(selected, entries);
  }
}

window.addEventListener('pointerdown', kbcgHandle, true);
window.addEventListener('click', kbcgHandle, true);
window.addEventListener('keydown', kbcgHandle, true);

document.documentElement.dataset.kachingBundleCartGuard = '2';